<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import {
  getWidgetLocaleMessages,
  readWidgetLocaleText,
} from 'dashboard/helper/widgetLocale';

const SCRIPT_OPEN = '\x3cscript\x3e';
const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';
const FIXED_ICON_SIZE = 24;

export default {
  name: 'PoweredBySettings',
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      state: {
        enabled: true,
        brandName: '',
        brandUrl: '',
        iconUrl: '',
      },
      pendingIconFile: null,
      originalSnapshot: '',
      isSaving: false,
      previewDocHtml: '',
    };
  },
  computed: {
    widgetLocaleMessages() {
      return getWidgetLocaleMessages(this.inbox.widget_language);
    },
    brandNamePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'YOU',
        'Your Brand'
      );
    },
    iconPreviewSize() {
      return FIXED_ICON_SIZE;
    },
    isDirty() {
      return this.snapshot() !== this.originalSnapshot;
    },
    saveDisabled() {
      return this.isSaving || !this.isDirty;
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    liveChatwootSettings() {
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const existingTheme = this.getThemeConfig(this.inbox);
      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode: isMidnight ? 'dark' : 'light',
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.inbox.widget_color || '#0059e1',
        iconColor: '#ffffff',
        themeConfig: {
          ...existingTheme,
          poweredBy: {
            enabled: !!this.state.enabled,
            text: 'Powered by',
            brandName: (this.state.brandName || '').trim(),
            brandUrl: (this.state.brandUrl || '').trim(),
            iconUrl: (this.state.iconUrl || '').trim(),
          },
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: 'messages',
      };
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler(next, prev) {
        const nextId = next?.id;
        const prevId = prev?.id;
        if (nextId && nextId !== prevId) {
          this.resetChanges();
          this.previewDocHtml = this.buildPreviewDoc();
        }
      },
    },
    state: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
  },
  mounted() {
    if (!this.previewDocHtml) {
      this.previewDocHtml = this.buildPreviewDoc();
    }
  },
  methods: {
    getThemeConfig(inbox = this.inbox) {
      const config = inbox?.widget_theme_config;
      if (!config || typeof config !== 'object' || Array.isArray(config)) {
        return {};
      }
      return config;
    },
    snapshot() {
      return JSON.stringify({
        enabled: !!this.state.enabled,
        brandName: (this.state.brandName || '').trim(),
        brandUrl: (this.state.brandUrl || '').trim(),
        iconUrl: (this.state.iconUrl || '').trim(),
      });
    },
    applyInboxState(inboxData) {
      const themeConfig = this.getThemeConfig(inboxData);
      const pb = themeConfig.poweredBy || {};
      this.state = {
        enabled: pb.enabled !== false,
        brandName: pb.brandName || '',
        brandUrl: pb.brandUrl || '',
        iconUrl: (pb.iconUrl || '').trim(),
      };
      this.pendingIconFile = null;
      this.originalSnapshot = this.snapshot();
    },
    removeUploadedIcon() {
      this.state.iconUrl = '';
      this.pendingIconFile = null;
    },
    onUploadFileChange(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      this.pendingIconFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.state.iconUrl = e.target.result;
      };
      reader.readAsDataURL(file);

      if (event?.target) event.target.value = '';
    },
    resetChanges() {
      this.applyInboxState(this.inbox);
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const bg =
        isMidnight && this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);

      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style>` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (_) {}
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async saveChanges() {
      if (this.saveDisabled || !this.inbox?.id) return;

      this.isSaving = true;
      try {
        if (this.pendingIconFile) {
          await InboxesAPI.uploadPoweredByIcon(
            this.inbox.id,
            this.pendingIconFile
          );
          await this.$store.dispatch('inboxes/get');
          const refreshed =
            this.$store.getters['inboxes/getInbox'](this.inbox.id) ||
            this.inbox;
          const refreshedConfig = this.getThemeConfig(refreshed);
          const pb = refreshedConfig.poweredBy || {};
          this.state.iconUrl = (pb.iconUrl || '').trim();
          this.pendingIconFile = null;
        }

        const existingThemeConfig = this.getThemeConfig(this.inbox);
        const nextThemeConfig = {
          ...existingThemeConfig,
          poweredBy: {
            enabled: !!this.state.enabled,
            text: 'Powered by',
            brandName: (this.state.brandName || '').trim(),
            brandUrl: (this.state.brandUrl || '').trim(),
            iconUrl: (this.state.iconUrl || '').trim(),
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          channel: {
            widget_theme_config: nextThemeConfig,
          },
        });

        await this.$store.dispatch('inboxes/get');
        const refreshed = this.$store.getters['inboxes/getInbox'](
          this.inbox.id
        );
        if (refreshed) {
          this.applyInboxState(refreshed);
        } else {
          this.originalSnapshot = this.snapshot();
        }

        useAlert('Powered by settings updated');
      } catch (error) {
        useAlert(error?.message || 'Failed to update settings');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="pbs-page">
    <div class="pbs-layout">
      <section class="pbs-settings-panel">
        <div class="pbs-header-bar">
          <div class="pbs-header-title">Powered by</div>
        </div>

        <form class="pbs-content-scroll" @submit.prevent="saveChanges">
          <div class="pbs-content-inner">
            <p class="pbs-desc">
              Configure branding at the bottom of the widget. You can toggle it
              on/off and customize brand name, destination URL, and icon.
            </p>

            <div class="pbs-card">
              <label class="pbs-toggle-row">
                <input
                  v-model="state.enabled"
                  type="checkbox"
                  class="pbs-toggle-input"
                />
                <span class="pbs-toggle-label">Show "Powered by" section</span>
              </label>
            </div>

            <template v-if="state.enabled">
              <div class="pbs-card">
                <div class="pbs-field-grid">
                  <div class="pbs-field">
                    <label class="pbs-field-label">Brand name</label>
                    <input
                      v-model="state.brandName"
                      class="pbs-input"
                      type="text"
                      :placeholder="brandNamePlaceholder"
                      spellcheck="false"
                    />
                  </div>
                  <div class="pbs-field">
                    <label class="pbs-field-label">Brand URL</label>
                    <input
                      v-model="state.brandUrl"
                      class="pbs-input"
                      type="url"
                      placeholder="https://example.com"
                      spellcheck="false"
                    />
                  </div>
                </div>
              </div>

              <div class="pbs-card">
                <label class="pbs-field-label">Brand icon</label>
                <p class="pbs-card-hint">
                  Upload a small icon ({{ iconPreviewSize }}x{{
                    iconPreviewSize
                  }}
                  px) to show beside the brand name.
                </p>

                <label class="pbs-upload">
                  <input
                    class="pbs-upload-input"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                    @change="onUploadFileChange"
                  />
                  <div class="pbs-upload-box">
                    <img
                      v-if="state.iconUrl"
                      :src="state.iconUrl"
                      class="pbs-upload-preview"
                      alt="Current icon"
                    />
                    <span v-else class="i-lucide-plus-circle pbs-upload-plus" />
                  </div>
                </label>

                <button
                  v-if="state.iconUrl"
                  type="button"
                  class="pbs-remove-icon-btn"
                  @click="removeUploadedIcon"
                >
                  Remove icon
                </button>
              </div>
            </template>
          </div>
        </form>

        <div class="pbs-save-bar">
          <button class="pbs-cancel-btn" type="button" @click="resetChanges">
            Cancel
          </button>
          <button
            class="pbs-save-btn"
            type="button"
            :disabled="saveDisabled"
            @click="saveChanges"
          >
            {{ isSaving ? 'Saving...' : 'Save changes' }}
          </button>
        </div>
      </section>

      <section class="pbs-preview-panel">
        <div class="pbs-preview-header-bar">
          <div class="pbs-preview-title">Preview</div>
        </div>

        <div class="pbs-preview-content-area">
          <div class="pbs-preview-scroll-inner">
            <div class="pbs-preview-widget-holder">
              <div class="pbs-preview-widget-stage">
                <div class="pbs-preview-widget-inner">
                  <iframe
                    v-if="previewDocHtml"
                    ref="sdkFrame"
                    class="pbs-preview-iframe"
                    :srcdoc="previewDocHtml"
                    title="Powered by preview"
                  />
                  <div v-else class="pbs-preview-empty">
                    Widget preview is unavailable for this inbox.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.pbs-page {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.pbs-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pbs-settings-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 296px;
  flex: 5 1 62.5%;
  background: #ffffff;
}

.pbs-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.pbs-header-title {
  margin: 0;
  height: 56px;
  line-height: 56px;
  padding-left: 21px;
  letter-spacing: 0.3px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.pbs-content-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.pbs-content-inner {
  width: 100%;
  max-width: 760px;
  padding: 32px 32px 120px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
}

.pbs-desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
}

.pbs-card {
  border: 1px solid #dde2e6;
  border-radius: 8px;
  padding: 14px 16px;
  background: #ffffff;
}

.pbs-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.pbs-toggle-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.pbs-toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.pbs-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.pbs-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pbs-field-label {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.pbs-input {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dde2e6;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.pbs-input:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.14);
}

.pbs-card-hint {
  margin: 6px 0 12px;
  font-size: 12px;
  color: rgba(17, 24, 39, 0.62);
}

.pbs-upload {
  display: inline-flex;
}

.pbs-upload-input {
  display: none;
}

.pbs-upload-box {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  border: 2px dashed #c8d2de;
  background: #f8fafc;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.pbs-upload-box:hover {
  border-color: #4384f5;
}

.pbs-upload-preview {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.pbs-upload-plus {
  font-size: 28px;
  color: #4384f5;
}

.pbs-remove-icon-btn {
  margin-top: 10px;
  border: none;
  background: transparent;
  color: #0059e1;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.pbs-remove-icon-btn:hover {
  text-decoration: underline;
}

.pbs-save-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  background: #fff;
  flex-shrink: 0;
}

.pbs-cancel-btn {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}

.pbs-cancel-btn:hover {
  background: #f5f5f5;
}

.pbs-save-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #0059e1;
  background: #0059e1;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    opacity 0.15s;
}

.pbs-save-btn:hover {
  background: #0047c2;
  border-color: #0047c2;
}

.pbs-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pbs-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.pbs-preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.pbs-preview-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  margin-left: 20px;
}

.pbs-preview-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
}

.pbs-preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.pbs-preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.pbs-preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.pbs-preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

.pbs-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.pbs-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.pbs-preview-test-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 16px;
}

.pbs-preview-test-note a {
  color: #0059e1;
  text-decoration: none;
}

.pbs-preview-test-note a:hover {
  text-decoration: underline;
}

@media (max-width: 1240px) {
  .pbs-layout {
    flex-direction: column;
  }

  .pbs-settings-panel {
    flex: 1 1 auto;
    border-bottom: 1px solid #eeeeef;
  }

  .pbs-preview-panel {
    border-left: none;
    flex: 1 1 auto;
  }
}

@media (max-width: 768px) {
  .pbs-content-inner {
    padding: 16px;
  }

  .pbs-save-bar {
    padding: 12px 16px;
  }

  .pbs-field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
